/* eslint-disable */
/**
 * Store IndexedDB du module Scan & Go (PWA client /panier).
 *
 * MODÈLE DE CONFIANCE - à lire avant de modifier ce fichier.
 *
 * Ce store est une donnée NON FIABLE, jamais une source d'autorité :
 *
 *  - VUE_APP_SECRET_KEY_INDEXDB est inlinée en clair dans le bundle par webpack.
 *    Le chiffrement empêche la lecture casuelle du DevTools ; ce n'est PAS une
 *    frontière de sécurité. La seule frontière est le backend.
 *  - Les prix ne quittent jamais le téléphone : le QR remis à la caisse ne
 *    contient que [[ean, qte]] (panier/index.vue, genererCodeCaisse). La caisse
 *    recalcule le total depuis sa propre base. Ne JAMAIS faire remonter un prix
 *    depuis le client.
 *  - La licence est revérifiée à chaque appel serveur : un état local trafiqué
 *    survit au plus jusqu'au prochain verifierAccesScanGo (borné par ttlSeconds).
 *
 * CONVENTION DE STOCKAGE - keyPath en clair, valeur chiffrée.
 * encryptData (CryptoJS AES en mode passphrase) n'est pas déterministe : salt et
 * IV aléatoires, donc deux chiffrements du même texte diffèrent et une clé
 * chiffrée ne peut jamais être retrouvée par index.get(). Les identifiants
 * (soc, mag, ean, sessionId) sont donc en clair - ce sont des identifiants
 * opaques - et seul le contenu sensible est chiffré dans un champ "blob".
 * Le catalogue (sgArticles) est intégralement en clair : le chiffrer imposerait
 * de déchiffrer des mégaoctets au démarrage, et CryptoJS est synchrone.
 */

import axios from "axios";
import { initDB, encryptData, decryptData } from "@/service/indexedDBService";
import { randomString } from "@/allfunction";

/**
 * Instance axios dédiée.
 *
 * ⚠️ Ne PAS utiliser l'instance axios globale : main.js y installe un
 * intercepteur de réponse dont le `return response` est commenté, si bien que
 * toute promesse résout avec undefined, et dont le handler d'erreur ne rejette
 * pas. Une instance créée par axios.create() n'hérite pas des intercepteurs
 * enregistrés sur l'instance par défaut.
 *
 * withCredentials: false - la PWA client est anonyme (aucun login). Aucun cookie
 * n'a de sens ici, et une requête « credentialed » serait de toute façon refusée
 * par le navigateur face au 'Access-Control-Allow-Origin: *' posé dans main.js.
 */
const sgHttp = axios.create({
    timeout: 8000,
    withCredentials: false,
});

const STORE_SESSIONS = "sgSessions";
const STORE_CATALOGUE_META = "sgCatalogueMeta";
const STORE_ARTICLES = "sgArticles";
const STORE_CART = "sgCart";
const STORE_DEVICE = "sgDevice";

const DEVICE_KEY = "device";

const TTL_SESSION = 24 * 3600 * 1000;      // garde-fou IDB ; la vraie fraîcheur = isSessionFresh
const TTL_CATALOGUE = 7 * 24 * 3600 * 1000;
const TTL_CART = 12 * 3600 * 1000;
const TTL_DEVICE = 365 * 24 * 3600 * 1000;

/**
 * Version du format d'article écrit par normArticle().
 *
 * Le checksum vient du serveur : il dit si le CONTENU a changé, jamais si notre
 * FORME de stockage a changé. Un catalogue écrit par une version antérieure
 * reste donc « valide » aux yeux du serveur tout en étant amputé des champs
 * ajoutés depuis - le menu restaurant afficherait une carte vide sans jamais
 * re-synchroniser. La méta porte donc ce numéro, et verifyCatalogueIntegrity
 * traite un écart comme une corruption : le rattrapage clearCatalogue +
 * refetch complet, déjà en place côté appelant, fait le reste.
 *
 * ⚠️ À incrémenter à CHAQUE changement des champs retournés par normArticle().
 *   1 → format d'origine (Scan & Go)
 *   2 → ajout de pu1..pu6, accompagnement, categorieplat (menu restaurant)
 *   3 → ajout de photo (images sur la carte du serveur et le panier)
 */
const CATALOGUE_SCHEMA = 3;

/** Clé de scope : un magasin d'une société. */
function scopeKey(soc, mag) {
    return `${String(soc || "")}|${String(mag || "")}`;
}

/** Clé d'un article dans sgArticles. */
function artKey(soc, mag, ean) {
    return `${scopeKey(soc, mag)}|${normEan(ean)}`;
}

function normEan(ean) {
    return String(ean == null ? "" : ean).trim();
}

/**
 * Déchiffre un blob et purge l'enregistrement si c'est illisible (rotation de
 * VUE_APP_SECRET_KEY_INDEXDB, corruption). L'appelant reçoit null et traite le
 * cas comme « cache absent » → re-fetch serveur. Auto-guérison, pas d'écran
 * d'erreur. decryptData ne throw pas : il retourne déjà null (indexedDBService).
 */
async function safeDecrypt(store, key, blob) {
    if (!blob) return null;
    let value = null;
    try {
        value = decryptData(blob);
    } catch (e) {
        value = null;
    }
    if (value == null) {
        console.warn(`[sg] blob illisible dans ${store} (${key}) - purge et re-sync`);
        try {
            const db = await initDB();
            await db.delete(store, key);
        } catch (e) { /* la purge est best-effort */ }
        return null;
    }
    return value;
}

function isExpired(record) {
    return !!(record && record.expiretime && Date.now() > record.expiretime);
}

/* ------------------------------------------------------------------ device */

function newUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Repli : crypto.randomUUID exige un contexte sécurisé, absent en HTTP sur LAN.
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        const b = crypto.getRandomValues(new Uint8Array(16));
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
        return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
    return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

/**
 * Identifiant d'appareil, stable et persistant. Sert au backend à compter les
 * appareils d'une licence. Idempotent.
 *
 * RGPD : c'est un identifiant en ligne, donc une donnée à caractère personnel
 * (art. 4.1). Base légale retenue : intérêt légitime (lutte contre l'abus de
 * licence). Il est effaçable via resetDeviceId. Ne jamais y adjoindre de
 * fingerprinting.
 */
async function getOrCreateDeviceId() {
    const db = await initDB();
    const rec = await db.get(STORE_DEVICE, DEVICE_KEY);
    if (rec) {
        const value = await safeDecrypt(STORE_DEVICE, DEVICE_KEY, rec.blob);
        if (value && value.deviceId) return value.deviceId;
    }
    const deviceId = newUuid();
    await db.put(STORE_DEVICE, {
        k: DEVICE_KEY,
        blob: encryptData({
            deviceId,
            createdAt: Date.now(),
            ua: String(navigator.userAgent || "").slice(0, 120),
        }),
        expiretime: Date.now() + TTL_DEVICE,
    });
    return deviceId;
}

/** Efface l'identifiant d'appareil (droit à l'effacement). */
async function resetDeviceId() {
    const db = await initDB();
    await db.delete(STORE_DEVICE, DEVICE_KEY);
}

/* -------------------------------------------------------------- sessionId */

/**
 * Référence de transaction présentée à la caisse.
 *
 * Remplace l'ancien "PAN-" + 4 chiffres, qui n'offrait que 10 000 valeurs :
 * par le paradoxe des anniversaires, deux clients partageaient la même
 * référence avec ~50 % de probabilité dès 118 paniers. Le segment deviceId
 * garantit en plus l'unicité entre appareils en cas de collision temporelle.
 *
 * ⚠️ Le format passe de 8 à ~24 caractères : la caisse doit l'accepter.
 */
function genSessionId(soc, mag, deviceId) {
    const t = Date.now().toString(36);
    const d = String(deviceId || "").replace(/-/g, "").slice(0, 6) || "xxxxxx";
    const r = Math.random().toString(36).slice(2, 6);
    return `PAN-${t}-${d}-${r}`;
}

/* ------------------------------------------------------------------ panier */

/**
 * Persiste le panier du magasin courant. Un seul panier actif par scope.
 * Le sessionId reste en clair à côté du blob : il permet de comparer les
 * onglets sans déchiffrer.
 */
async function saveCart(soc, mag, sessionId, cart) {
    const db = await initDB();
    await db.put(STORE_CART, {
        k: scopeKey(soc, mag),
        sessionId: String(sessionId || ""),
        blob: encryptData({
            items: (cart && cart.items) || [],
            view: (cart && cart.view) || "scan",
            createdAt: (cart && cart.createdAt) || Date.now(),
        }),
        updatedAt: Date.now(),
        expiretime: Date.now() + TTL_CART,
    });
}

async function getCart(soc, mag) {
    const db = await initDB();
    const key = scopeKey(soc, mag);
    const rec = await db.get(STORE_CART, key);
    if (!rec) return null;
    if (isExpired(rec)) {
        await db.delete(STORE_CART, key);
        return null;
    }
    const value = await safeDecrypt(STORE_CART, key, rec.blob);
    if (!value) return null;
    return {
        sessionId: rec.sessionId,
        items: Array.isArray(value.items) ? value.items : [],
        view: value.view || "scan",
        createdAt: value.createdAt,
        updatedAt: rec.updatedAt,
    };
}

async function clearCart(soc, mag) {
    const db = await initDB();
    await db.delete(STORE_CART, scopeKey(soc, mag));
}

/* ----------------------------------------------------------- session/licence */

/**
 * Enregistre une vérification réussie.
 *
 * On mémorise DEUX horodatages : celui du serveur (autoritaire, pour l'affichage
 * et le diagnostic) et celui du client au même instant (verifiedAtLocal), qui
 * sert de point de référence à isSessionFresh.
 */
async function saveSession(soc, mag, session) {
    const db = await initDB();
    const serverNow = Number(session.serverNow) || Date.now();
    const ttlSeconds = Number(session.ttlSeconds) || 3600;
    await db.put(STORE_SESSIONS, {
        k: scopeKey(soc, mag),
        blob: encryptData({
            key: session.key,
            quota: session.quota || null,
            magLabel: session.magLabel || "",
            socLabel: session.socLabel || "",
        }),
        verifiedAtServer: serverNow,
        verifiedAtLocal: Date.now(),
        validUntilServer: serverNow + ttlSeconds * 1000,
        ttlSeconds,
        expiretime: Date.now() + TTL_SESSION,
    });
}

async function getSession(soc, mag) {
    const db = await initDB();
    const key = scopeKey(soc, mag);
    const rec = await db.get(STORE_SESSIONS, key);
    if (!rec) return null;
    if (isExpired(rec)) {
        await db.delete(STORE_SESSIONS, key);
        return null;
    }
    const value = await safeDecrypt(STORE_SESSIONS, key, rec.blob);
    if (!value) return null;
    return {
        key: value.key,
        quota: value.quota,
        magLabel: value.magLabel,
        socLabel: value.socLabel,
        verifiedAtServer: rec.verifiedAtServer,
        verifiedAtLocal: rec.verifiedAtLocal,
        validUntilServer: rec.validUntilServer,
        ttlSeconds: rec.ttlSeconds,
    };
}

/**
 * La session est-elle encore fraîche ?
 *
 * Date.now() est falsifiable, donc on ne compare JAMAIS l'heure courante à un
 * instant absolu venu du serveur. On mesure une DURÉE ÉCOULÉE depuis un point
 * de référence local :
 *   - avancer l'horloge → elapsed plus grand → expire plus tôt → re-vérif serveur ;
 *   - reculer l'horloge → elapsed négatif → refus immédiat → re-vérif serveur.
 * Un décalage constant est ainsi neutralisé dans les deux sens. validUntilServer
 * n'est conservé que pour l'affichage.
 */
function isSessionFresh(session) {
    if (!session || !session.verifiedAtLocal || !session.ttlSeconds) return false;
    const elapsed = Date.now() - session.verifiedAtLocal;
    if (elapsed < 0) return false;
    return elapsed < session.ttlSeconds * 1000;
}

async function clearSession(soc, mag) {
    const db = await initDB();
    await db.delete(STORE_SESSIONS, scopeKey(soc, mag));
}

/* ------------------------------------------------------- vérification backend */

/**
 * Vérifie l'accès au Scan & Go auprès du backend.
 *
 * POST et non GET : la clé fait ~280 caractères et n'a rien à faire dans les
 * logs d'un proxy. Pas d'en-tête Authorization : la PWA est anonyme, et
 * 'Bearer null' ferait échouer un backend strict.
 *
 * Ne lève jamais : retourne un objet discriminé. La distinction entre un refus
 * métier ('denied') et une panne réseau ('network') est essentielle - une
 * coupure réseau ne doit jamais refuser l'accès à un client légitime.
 */
async function verifierAccesScanGo({ soc, mag, key, deviceId, catalogueVersion, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/verifyaccess/${encodeURIComponent(soc)}/${encodeURIComponent(mag)}/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { soc, mag, key, deviceId, catalogueVersion: catalogueVersion || null },
            {
                signal,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "csrftoken": soc,
                },
            }
        );
    } catch (e) {
        if (e && (e.code === "ERR_CANCELED" || e.name === "CanceledError")) {
            return { status: "network", reason: "canceled" };
        }
        if (e && e.code === "ECONNABORTED") return { status: "network", reason: "timeout" };
        if (!navigator.onLine) return { status: "network", reason: "offline" };
        if (e && e.response) return { status: "network", reason: "server", http: e.response.status };
        return { status: "network", reason: "offline" };
    }

    const body = res && res.data;
    if (!body || typeof body.success === "undefined") {
        // Réponse inattendue : on la traite comme une panne, jamais comme un refus.
        return { status: "network", reason: "malformed" };
    }

    if (body.success === true) {
        return { status: "ok", data: body.m || {} };
    }

    const m = body.m || {};
    return {
        status: "denied",
        code: m.code || "UNKNOWN",
        message: m.message || "",
        retryable: !!m.retryable,
        details: m.details || null,
    };
}

/* ------------------------------------------------------------------ tables */

/**
 * Charge les tables de salle de la société (PWA serveur).
 *
 * Endpoint dédié : les tables sont un référentiel société, résolu à partir de
 * la seule clé de licence (pas de soc/mag dans le chemin). Même contrat de
 * retour que verifierAccesScanGo : `ok` porte les tables, `network` laisse
 * l'appelant retomber sur le cache, `denied` signale une clé invalide.
 *
 * @returns {Promise<{status:'ok'|'network'|'denied', tables?:Array, code?:string, reason?:string}>}
 */
async function chargerTablesScanGo({ key, deviceId, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/tables/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, deviceId },
            {
                signal,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "csrftoken": key,
                },
            }
        );
    } catch (e) {
        if (e && (e.code === "ERR_CANCELED" || e.name === "CanceledError")) {
            return { status: "network", reason: "canceled" };
        }
        if (e && e.code === "ECONNABORTED") return { status: "network", reason: "timeout" };
        if (!navigator.onLine) return { status: "network", reason: "offline" };
        if (e && e.response) return { status: "network", reason: "server", http: e.response.status };
        return { status: "network", reason: "offline" };
    }

    const body = res && res.data;
    if (!body || typeof body.success === "undefined") {
        return { status: "network", reason: "malformed" };
    }

    if (body.success === true) {
        const m = body.m || {};
        return { status: "ok", tables: Array.isArray(m.tables) ? m.tables : [] };
    }

    const m = body.m || {};
    return { status: "denied", code: m.code || "UNKNOWN", reason: m.message || "" };
}

/**
 * Ingrédients qu'un client peut faire retirer ou doubler, par plat (PWA serveur).
 *
 * Même contrat que chargerTablesScanGo : `ok` porte la liste (indexée par ean),
 * `network` laisse l'appelant retomber sur son cache, `denied` signale une clé
 * invalide. Une liste vide est une réponse normale (société sans fiche réglée).
 *
 * @returns {Promise<{status:'ok'|'network'|'denied', articles?:object, code?:string, reason?:string}>}
 */
async function chargerIngredientsScanGo({ key, deviceId, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/ingredients/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, deviceId },
            {
                signal,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "csrftoken": key,
                },
            }
        );
    } catch (e) {
        if (e && (e.code === "ERR_CANCELED" || e.name === "CanceledError")) {
            return { status: "network", reason: "canceled" };
        }
        if (e && e.code === "ECONNABORTED") return { status: "network", reason: "timeout" };
        if (!navigator.onLine) return { status: "network", reason: "offline" };
        if (e && e.response) return { status: "network", reason: "server", http: e.response.status };
        return { status: "network", reason: "offline" };
    }

    const body = res && res.data;
    if (!body || typeof body.success === "undefined") {
        return { status: "network", reason: "malformed" };
    }

    const m = body.m || {};
    if (body.success === true) {
        return { status: "ok", articles: (m.articles && typeof m.articles === "object") ? m.articles : {} };
    }
    return { status: "denied", code: m.code || "UNKNOWN", reason: m.message || "" };
}

/**
 * Poll de l'état d'encaissement (PWA serveur).
 *
 * Sans WebSocket, le téléphone interroge le backend : « ces commandes (srvcmdid)
 * ont-elles été encaissées ? ». Route publique gated par la clé ; `ids` est la
 * liste des identifiants à vérifier, `deviceId` sert au rate-limiter.
 *
 * @returns {Promise<{status:'ok'|'network'|'denied', encaisses?:Array, code?:string}>}
 *          `encaisses` : [{ srvcmdid, docnum, montant, encaisse_at }, ...]
 */
async function statutEncaissementScanGo({ key, deviceId, ids, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/statutencaissement/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, deviceId, ids: Array.isArray(ids) ? ids : [] },
            {
                signal,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "csrftoken": key,
                },
            }
        );
    } catch (e) {
        if (e && (e.code === "ERR_CANCELED" || e.name === "CanceledError")) {
            return { status: "network", reason: "canceled" };
        }
        if (e && e.code === "ECONNABORTED") return { status: "network", reason: "timeout" };
        if (!navigator.onLine) return { status: "network", reason: "offline" };
        if (e && e.response) return { status: "network", reason: "server", http: e.response.status };
        return { status: "network", reason: "offline" };
    }

    const body = res && res.data;
    if (!body || typeof body.success === "undefined") {
        return { status: "network", reason: "malformed" };
    }
    if (body.success === true) {
        const m = body.m || {};
        return { status: "ok", encaisses: Array.isArray(m.encaisses) ? m.encaisses : [] };
    }
    const m = body.m || {};
    return { status: "denied", code: m.code || "UNKNOWN" };
}

/**
 * Statut d'une liste de commandes serveur (poll de confirmation).
 *
 * Filet du canal temps réel : quand l'accusé `ack` WebSocket a été manqué
 * (téléphone en veille, coupure), le serveur interroge ce point de rendez-vous
 * pour savoir si la caisse a chargé/encaissé sa commande. Vaut AUSSI en mode QR
 * pur : la caisse écrit le statut à l'ingestion, quel que soit le transport.
 * Route publique gated par la clé ; `ids` = liste des srvcmdid à vérifier.
 *
 * @returns {Promise<{status:'ok'|'network'|'denied', commandes?:Array, code?:string}>}
 *          `commandes` : [{ srvcmdid, statut, docnum, updated_at }, ...]
 */
async function statutCommandeScanGo({ key, deviceId, ids, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/statutcommande/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, deviceId, ids: Array.isArray(ids) ? ids : [] },
            {
                signal,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "csrftoken": key,
                },
            }
        );
    } catch (e) {
        if (e && (e.code === "ERR_CANCELED" || e.name === "CanceledError")) {
            return { status: "network", reason: "canceled" };
        }
        if (e && e.code === "ECONNABORTED") return { status: "network", reason: "timeout" };
        if (!navigator.onLine) return { status: "network", reason: "offline" };
        if (e && e.response) return { status: "network", reason: "server", http: e.response.status };
        return { status: "network", reason: "offline" };
    }

    const body = res && res.data;
    if (!body || typeof body.success === "undefined") {
        return { status: "network", reason: "malformed" };
    }
    if (body.success === true) {
        const m = body.m || {};
        return { status: "ok", commandes: Array.isArray(m.commandes) ? m.commandes : [] };
    }
    const m = body.m || {};
    return { status: "denied", code: m.code || "UNKNOWN" };
}

/**
 * Journal d'événements des commandes (LECTURE - PWA serveur, gated clé).
 * nonlus=true → seulement les non-lus (poll, déclenche la notif) ;
 * nonlus=false → historique complet (à l'ouverture d'une commande).
 * @returns {Promise<{status:'ok'|'network'|'denied', evenements?:Array, code?:string}>}
 */
async function evenementsCommandeScanGo({ key, deviceId, ids, nonlus, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/evenementscommande/${randomString()}`;
    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, deviceId, ids: Array.isArray(ids) ? ids : [], nonlus: !!nonlus },
            { signal, headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "application/json", "Content-Type": "application/json", "csrftoken": key } }
        );
    } catch (e) {
        if (e && (e.code === "ERR_CANCELED" || e.name === "CanceledError")) return { status: "network", reason: "canceled" };
        if (e && e.code === "ECONNABORTED") return { status: "network", reason: "timeout" };
        if (!navigator.onLine) return { status: "network", reason: "offline" };
        if (e && e.response) return { status: "network", reason: "server", http: e.response.status };
        return { status: "network", reason: "offline" };
    }
    const body = res && res.data;
    if (!body || typeof body.success === "undefined") return { status: "network", reason: "malformed" };
    if (body.success === true) {
        const m = body.m || {};
        return { status: "ok", evenements: Array.isArray(m.evenements) ? m.evenements : [] };
    }
    const m = body.m || {};
    return { status: "denied", code: m.code || "UNKNOWN" };
}

/**
 * Marque des événements comme lus (PWA serveur, gated clé). ids = idEvenement[].
 * @returns {Promise<{status:'ok'|'network'|'denied', n?:number}>}
 */
async function marquerLuEvenementScanGo({ key, deviceId, ids, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/marquerluevenement/${randomString()}`;
    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, deviceId, ids: Array.isArray(ids) ? ids : [] },
            { signal, headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "application/json", "Content-Type": "application/json", "csrftoken": key } }
        );
    } catch (e) {
        return { status: "network" };
    }
    const body = res && res.data;
    if (!body || typeof body.success === "undefined") return { status: "network" };
    if (body.success === true) return { status: "ok", n: (body.m && body.m.n) || 0 };
    return { status: "denied" };
}

/**
 * Cache des tables - stocké dans sgCatalogueMeta sous une clé DISTINCTE des
 * scopes catalogue (`<key>|tables` ≠ `<soc>|<mag>`), pour ne PAS créer de
 * nouveau store (le bump de DB_VERSION est destructif). Le rafraîchissement est
 * réseau-first à chaque prise de service ; ce cache n'est qu'un repli hors ligne.
 */
const TTL_TABLES = 7 * 24 * 3600 * 1000;

function tablesKey(key) {
    return `${String(key || "")}|tables`;
}

async function saveTables(key, tables) {
    const db = await initDB();
    await db.put(STORE_CATALOGUE_META, {
        k: tablesKey(key),
        tables: Array.isArray(tables) ? tables : [],
        cachedAt: Date.now(),
        expiretime: Date.now() + TTL_TABLES,
    });
}

/** @returns {Promise<Array|null>} null si aucun cache (jamais chargé) ou périmé. */
async function getTables(key) {
    const db = await initDB();
    const rec = await db.get(STORE_CATALOGUE_META, tablesKey(key));
    if (!rec) return null;
    if (rec.expiretime && rec.expiretime < Date.now()) {
        await db.delete(STORE_CATALOGUE_META, tablesKey(key));
        return null;
    }
    return Array.isArray(rec.tables) ? rec.tables : [];
}

/**
 * Cache des ingrédients modifiables : même rangement que les tables (clé
 * distincte dans sgCatalogueMeta, pas de nouveau store), même durée de vie.
 */
function ingredientsKey(key) {
    return `${String(key || "")}|ingredients`;
}

async function saveIngredients(key, articles) {
    const db = await initDB();
    await db.put(STORE_CATALOGUE_META, {
        k: ingredientsKey(key),
        articles: (articles && typeof articles === "object") ? articles : {},
        cachedAt: Date.now(),
        expiretime: Date.now() + TTL_TABLES,
    });
}

/** @returns {Promise<object|null>} null si aucun cache (jamais chargé) ou périmé. */
async function getIngredients(key) {
    const db = await initDB();
    const rec = await db.get(STORE_CATALOGUE_META, ingredientsKey(key));
    if (!rec) return null;
    if (rec.expiretime && rec.expiretime < Date.now()) {
        await db.delete(STORE_CATALOGUE_META, ingredientsKey(key));
        return null;
    }
    return (rec.articles && typeof rec.articles === "object") ? rec.articles : {};
}

/* --------------------------------------------------------------- catalogue */

const CHUNK_SIZE = 1000;

function normArticle(soc, mag, a) {
    return {
        k: artKey(soc, mag, a.ean),
        scope: scopeKey(soc, mag),
        ean: normEan(a.ean),
        libelle: a.libelle,
        prix: Number(a.prix) || 0,
        pu: Number(a.pu) || 0,
        pv: Array.isArray(a.pv) ? a.pv : [],
        pu7: Number(a.pu7) || 0,
        unite: a.unite || "",
        actif: a.actif !== false,
        emplacement: a.emplacement || null,

        // Champs restaurant. Le backend les envoie depuis toujours (lus dans
        // dbarticles.datajsonrestaurant) mais ils étaient jetés ici : le module
        // menu ne pouvait ni classer les plats par catégorie, ni proposer
        // d'accompagnement. Le panier, lui, les ignore - l'ajout est neutre
        // pour lui. Toute modification de cette liste impose de bumper
        // CATALOGUE_SCHEMA.
        pu1: Number(a.pu1) || 0,
        pu2: Number(a.pu2) || 0,
        pu3: Number(a.pu3) || 0,
        pu4: Number(a.pu4) || 0,
        pu5: Number(a.pu5) || 0,
        pu6: Number(a.pu6) || 0,
        accompagnement: Array.isArray(a.accompagnement) ? a.accompagnement : [],
        categorieplat: a.categorieplat || "",

        // Chemin de la photo de l'article, "" s'il n'y en a pas (cf. imagesArticles.js).
        photo: a.photo || "",
    };
}

/**
 * Remplace le catalogue d'un magasin.
 *
 * Les écritures sont découpées : une transaction unique de 50 000 put fige
 * l'écran. Entre deux lots, on rend la main au thread UI.
 *
 * sgCatalogueMeta est écrit EN DERNIER : si le navigateur est fermé en cours
 * de route, l'absence de méta rend le catalogue partiel invisible (getCatalogueMeta
 * renvoie null) et force un re-sync propre plutôt que d'exposer un catalogue
 * tronqué dans lequel des articles seraient « introuvables ».
 */
async function saveCatalogue(soc, mag, articles, meta, onProgress) {
    const db = await initDB();
    const list = Array.isArray(articles) ? articles : [];

    await clearArticlesByScope(scopeKey(soc, mag));

    for (let i = 0; i < list.length; i += CHUNK_SIZE) {
        const tx = db.transaction(STORE_ARTICLES, "readwrite");
        for (const a of list.slice(i, i + CHUNK_SIZE)) {
            tx.store.put(normArticle(soc, mag, a));
        }
        await tx.done;
        if (typeof onProgress === "function") {
            onProgress(Math.min(i + CHUNK_SIZE, list.length), list.length);
        }
        await new Promise((r) => setTimeout(r, 0));   // laisse respirer l'UI
    }

    await db.put(STORE_CATALOGUE_META, {
        k: scopeKey(soc, mag),
        version: (meta && meta.version) || null,
        count: list.length,
        checksum: (meta && meta.checksum) || null,
        schema: CATALOGUE_SCHEMA,
        syncedAtServer: (meta && meta.serverNow) || Date.now(),
        expiretime: Date.now() + TTL_CATALOGUE,
    });

    // Le cache mémoire décrit le catalogue d'AVANT ce remplacement.
    invalidateArticleCache(soc, mag);
}

async function getCatalogueMeta(soc, mag) {
    const db = await initDB();
    const key = scopeKey(soc, mag);
    const rec = await db.get(STORE_CATALOGUE_META, key);
    if (!rec) return null;
    if (isExpired(rec)) {
        await clearCatalogue(soc, mag);
        return null;
    }
    return {
        version: rec.version,
        count: rec.count,
        checksum: rec.checksum,
        // Absent des métas écrites avant l'introduction du versionnage : le
        // `|| 1` les identifie comme schéma d'origine, donc périmées.
        schema: rec.schema || 1,
        syncedAtServer: rec.syncedAtServer,
    };
}

/**
 * Repousse l'échéance du catalogue sans le retélécharger.
 *
 * À appeler quand le serveur répond `unchanged` : il vient de confirmer que
 * notre copie est la bonne, elle doit donc repartir pour un tour complet.
 * Sans ça, `expiretime` restait figé à la date du dernier `full` et le
 * catalogue était purgé au bout de TTL_CATALOGUE même pour un client qui
 * synchronisait tous les jours - le TTL se comportait en durée absolue au lieu
 * d'une fenêtre glissante, et forçait un rechargement complet inutile.
 *
 * version/count/checksum ne bougent pas : rien n'a changé, par définition.
 */
async function touchCatalogueMeta(soc, mag, serverNow) {
    const db = await initDB();
    const key = scopeKey(soc, mag);
    const rec = await db.get(STORE_CATALOGUE_META, key);
    if (!rec) return false;
    await db.put(STORE_CATALOGUE_META, {
        ...rec,
        syncedAtServer: serverNow || Date.now(),
        expiretime: Date.now() + TTL_CATALOGUE,
    });
    return true;
}

/** Le cœur du scan : lookup direct en O(1), sans rien charger en mémoire. */
async function getArticleByEan(soc, mag, ean) {
    const code = normEan(ean);
    if (!code) return null;
    const db = await initDB();
    const rec = await db.get(STORE_ARTICLES, artKey(soc, mag, code));
    if (!rec || rec.actif === false) return null;
    return rec;
}

/**
 * Recherche d'articles par libellé (nom).
 * Parcourt le scopeIndex avec un curseur et filtre par substring.
 * Limité à 50 résultats pour la réactivité.
 */
/*async function searchArticlesByName(soc, mag, query) {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return [];
    const db = await initDB();
    const results = [];
    const scope = scopeKey(soc, mag);
    const cursor = await db.transaction(STORE_ARTICLES).store
        .index("scopeIndex").openCursor(IDBKeyRange.only(scope));
    while (cursor) {
        const lib = (cursor.value.libelle || "").toLowerCase();
        if (lib.includes(q)) {
            results.push(cursor.value);
            if (results.length >= 50) break;
        }
        cursor = await cursor.continue();
    }
    return results;
}*/

// Cache en mémoire par scope
const _articleCache = new Map(); // scope -> [{ item, libLower }]

async function _loadScopeCache(db, scope) {
    let entry = _articleCache.get(scope);
    if (entry) return entry;

    const raw = await db.getAllFromIndex(
        STORE_ARTICLES, "scopeIndex", IDBKeyRange.only(scope)
    );
    entry = {
        items: raw.map(item => ({
            item,
            libLower: (item.libelle || "").toLowerCase(),
        })),
    };
    _articleCache.set(scope, entry);
    return entry;
}

// À appeler après tout ajout / modif / suppression d'article
function invalidateArticleCache(soc, mag) {
    if (soc === undefined) _articleCache.clear();
    else _articleCache.delete(scopeKey(soc, mag));
}

let _searchToken = 0;

async function searchArticlesByName(soc, mag, query, limit = 50) {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return [];

    const myToken = ++_searchToken; // pour annuler les recherches obsolètes
    const db = await initDB();
    const scope = scopeKey(soc, mag);
    const { items } = await _loadScopeCache(db, scope);

    if (myToken !== _searchToken) return []; // une recherche plus récente a été lancée

    const starts = [];
    const contains = [];

    for (let i = 0; i < items.length; i++) {
        const idx = items[i].libLower.indexOf(q);
        if (idx === -1) continue;
        if (idx === 0) {
            starts.push(items[i].item);
            if (starts.length >= limit) break; // priorité aux libellés qui commencent par la recherche
        } else if (contains.length < limit) {
            contains.push(items[i].item);
        }
    }

    if (starts.length < limit) {
        for (let i = 0; i < contains.length && starts.length < limit; i++) {
            starts.push(contains[i]);
        }
    }

    return starts;
}



/**
 * Retourne TOUT le catalogue d'un magasin.
 *
 * Le menu restaurant affiche la carte entière, il ne cherche pas : ni
 * getArticleByEan (lookup unitaire) ni searchArticlesByName (qui renvoie []
 * sur requête vide et plafonne à `limit`) ne conviennent. On réutilise le
 * cache mémoire de la recherche - même scope, mêmes données, un seul balayage.
 *
 * Réservé aux catalogues de taille « carte de restaurant ». Sur les ~50 000
 * articles d'un supermarché, cela charge tout en mémoire : ne pas l'appeler
 * depuis le parcours Scan & Go, qui a getArticleByEan pour ça.
 */
async function getAllArticles(soc, mag) {
    const db = await initDB();
    const { items } = await _loadScopeCache(db, scopeKey(soc, mag));
    return items.map((e) => e.item);
}

/** Applique un delta serveur : un put/delete par article touché. */
async function applyCatalogueDelta(soc, mag, delta, meta) {
    const db = await initDB();
    const upsert = (delta && delta.upsert) || [];
    const remove = (delta && delta.remove) || [];

    for (let i = 0; i < upsert.length; i += CHUNK_SIZE) {
        const tx = db.transaction(STORE_ARTICLES, "readwrite");
        for (const a of upsert.slice(i, i + CHUNK_SIZE)) {
            tx.store.put(normArticle(soc, mag, a));
        }
        await tx.done;
        await new Promise((r) => setTimeout(r, 0));
    }

    if (remove.length) {
        const tx = db.transaction(STORE_ARTICLES, "readwrite");
        for (const ean of remove) tx.store.delete(artKey(soc, mag, ean));
        await tx.done;
    }

    const count = await countArticles(soc, mag);
    await db.put(STORE_CATALOGUE_META, {
        k: scopeKey(soc, mag),
        version: (meta && meta.version) || null,
        count,
        checksum: (meta && meta.checksum) || null,
        schema: CATALOGUE_SCHEMA,
        syncedAtServer: (meta && meta.serverNow) || Date.now(),
        expiretime: Date.now() + TTL_CATALOGUE,
    });

    invalidateArticleCache(soc, mag);
}

async function countArticles(soc, mag) {
    const db = await initDB();
    return db.countFromIndex(STORE_ARTICLES, "scopeIndex", IDBKeyRange.only(scopeKey(soc, mag)));
}

/**
 * Détecte une troncature ou une corruption du catalogue en comparant le nombre
 * d'articles réellement présents à celui annoncé par la méta.
 *
 * ⚠️ Ce n'est PAS une barrière de sécurité : qui modifie le store peut aussi
 * corriger le count. Ça n'attrape que la corruption accidentelle (écriture
 * interrompue, éviction partielle) et le bidouillage casuel. Cf. le modèle de
 * confiance en tête de fichier : la seule autorité est le backend.
 */
async function verifyCatalogueIntegrity(soc, mag, meta) {
    if (!meta || typeof meta.count !== "number") return false;

    // Écrit par une version antérieure de normArticle() : le contenu est peut-être
    // à jour, mais il lui manque des champs. Le serveur ne peut pas le détecter
    // (son checksum ne porte que sur les données), donc c'est ici que ça se joue.
    if ((meta.schema || 1) !== CATALOGUE_SCHEMA) {
        console.warn(`[sg] catalogue au schéma ${meta.schema || 1} (attendu ${CATALOGUE_SCHEMA}) - re-sync`);
        return false;
    }

    const count = await countArticles(soc, mag);
    if (count !== meta.count) {
        console.warn(`[sg] catalogue incohérent (${count} présents / ${meta.count} attendus) - re-sync`);
        return false;
    }
    // Cette fonction ne répond QU'À une question : le contenu correspond-il à
    // ce qui est annoncé ? Un `count > 0` ici déclarerait invalide un magasin
    // réellement vide (0 === 0, donc parfaitement cohérent) et provoquerait
    // deux appels réseau à chaque ouverture, indéfiniment. Le cas « catalogue
    // vide » se règle au scan, par un « produit introuvable » - qui est exact.
    return true;
}

async function clearCatalogue(soc, mag) {
    const db = await initDB();
    await clearArticlesByScope(scopeKey(soc, mag));
    await db.delete(STORE_CATALOGUE_META, scopeKey(soc, mag));
}

/* ------------------------------------------------------------------ purges */

/**
 * Supprime tous les articles d'un scope. Passe par scopeIndex avec une plage
 * restreinte : seules les lignes à supprimer sont visitées. Ne jamais itérer
 * sgArticles sans borne - ce store peut contenir ~50 000 lignes.
 */
async function clearArticlesByScope(scope) {
    const db = await initDB();
    const tx = db.transaction(STORE_ARTICLES, "readwrite");
    const index = tx.store.index("scopeIndex");
    let cursor = await index.openKeyCursor(IDBKeyRange.only(scope));
    const keys = [];
    while (cursor) {
        keys.push(cursor.primaryKey);
        cursor = await cursor.continue();
    }
    for (const k of keys) tx.store.delete(k);
    await tx.done;

    // Le scope est déjà sous forme de clé ici : on ne peut pas passer par
    // invalidateArticleCache(soc, mag), qui la recompose.
    _articleCache.delete(scope);
}

async function clearArticles(soc, mag) {
    return clearArticlesByScope(scopeKey(soc, mag));
}

/**
 * Purge tout ce qui n'appartient pas au magasin courant. Appelé au mount : un
 * client qui scanne le QR du magasin B ne doit rien conserver du magasin A.
 *
 * Les scopes connus sont déduits des stores à un enregistrement par scope
 * (quelques clés), puis les articles ne sont purgés que pour ces scopes-là.
 * On évite ainsi de balayer sgArticles en entier à chaque mount.
 */
async function purgeOtherScopes(keepSoc, keepMag) {
    const db = await initDB();
    const keep = scopeKey(keepSoc, keepMag);

    // Recenser AVANT de supprimer les métas, sinon on perd la trace des scopes.
    const scopes = new Set();
    for (const store of [STORE_SESSIONS, STORE_CATALOGUE_META, STORE_CART]) {
        for (const k of await db.getAllKeys(store)) {
            if (k !== keep) scopes.add(k);
        }
    }

    for (const store of [STORE_SESSIONS, STORE_CATALOGUE_META, STORE_CART]) {
        for (const k of await db.getAllKeys(store)) {
            if (k !== keep) await db.delete(store, k);
        }
    }

    for (const scope of scopes) await clearArticlesByScope(scope);
}

/**
 * Pousse au backend la photo CHIFFRÉE du store d'un serveur (identité + commandes).
 * Filet anti-perte : mise à jour de la PWA, panne ou changement de téléphone.
 * Route publique gated par la clé ; `blob` est un ciphertext opaque pour le serveur.
 *
 * @returns {Promise<{status:'ok'|'network'|'denied', code?:string}>}
 */
async function sauvegarderStoreServeur({ key, serveurPid, deviceId, blob, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/sauvestoreserveur/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, serveurPid, deviceId, blob: typeof blob === "string" ? blob : "" },
            { signal, headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "application/json", "Content-Type": "application/json", "csrftoken": key } }
        );
    } catch (e) {
        return { status: "network" };
    }
    const body = res && res.data;
    if (!body || typeof body.success === "undefined") return { status: "network" };
    if (body.success === true) return { status: "ok" };
    return { status: "denied", code: (body.m && body.m.code) || "UNKNOWN" };
}

/**
 * Récupère du backend la dernière photo chiffrée du store d'un serveur.
 * @returns {Promise<{status:'ok'|'network'|'denied', blob?:string|null, code?:string}>}
 */
async function chargerStoreServeur({ key, serveurPid, deviceId, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/chargestoreserveur/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { key, serveurPid, deviceId },
            { signal, headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "application/json", "Content-Type": "application/json", "csrftoken": key } }
        );
    } catch (e) {
        return { status: "network" };
    }
    const body = res && res.data;
    if (!body || typeof body.success === "undefined") return { status: "network" };
    if (body.success === true) {
        const m = body.m || {};
        return { status: "ok", blob: m.blob || null, updatedAt: m.updatedAt || null };
    }
    return { status: "denied", code: (body.m && body.m.code) || "UNKNOWN" };
}

/**
 * Résout un lien court (code du QR) en sa cible réelle. Route PUBLIQUE : la page
 * interstitielle n'a ni session ni clé, seulement le code lu dans l'URL.
 * @returns {Promise<{status:'ok'|'network'|'denied', cible?:string, code?:string}>}
 *          denied avec code = 'INVALID' (inconnu) ou 'EXPIRED' (dépassé).
 */
async function resoudreShortlink({ code, deviceId, signal }) {
    const url = `${process.env.VUE_APP_API_URL}/egl745874pscango771714dfe/pwa/othetdskd8755dsdhisd/resolve/${randomString()}`;

    let res;
    try {
        res = await sgHttp.post(
            url,
            { code, deviceId },
            { signal, headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "application/json", "Content-Type": "application/json" } }
        );
    } catch (e) {
        return { status: "network" };
    }
    const body = res && res.data;
    if (!body || typeof body.success === "undefined") return { status: "network" };
    if (body.success === true) return { status: "ok", cible: (body.m && body.m.cible) || "" };
    return { status: "denied", code: (body.m && body.m.code) || "INVALID" };
}

export {
    scopeKey,
    artKey,
    getOrCreateDeviceId,
    resetDeviceId,
    genSessionId,
    saveSession,
    getSession,
    isSessionFresh,
    clearSession,
    verifierAccesScanGo,
    saveCart,
    getCart,
    clearCart,
    saveCatalogue,
    getCatalogueMeta,
    touchCatalogueMeta,
    getArticleByEan,
    searchArticlesByName,
    getAllArticles,
    invalidateArticleCache,
    applyCatalogueDelta,
    verifyCatalogueIntegrity,
    countArticles,
    clearCatalogue,
    clearArticles,
    purgeOtherScopes,
    chargerTablesScanGo,
    saveTables,
    getTables,
    chargerIngredientsScanGo,
    saveIngredients,
    getIngredients,
    statutEncaissementScanGo,
    statutCommandeScanGo,
    evenementsCommandeScanGo,
    marquerLuEvenementScanGo,
    sauvegarderStoreServeur,
    chargerStoreServeur,
    resoudreShortlink,
    // internes, réexportés pour les étapes suivantes
    safeDecrypt,
    isExpired,
    normEan,
    STORE_SESSIONS,
    STORE_CATALOGUE_META,
    STORE_ARTICLES,
    STORE_CART,
    STORE_DEVICE,
    TTL_SESSION,
    TTL_CATALOGUE,
    TTL_CART,
};
